export const RECAPTCHA_ACTION = "ai_triage_chat";
export const RECAPTCHA_SCRIPT_ATTR = "data-ai-triage-recaptcha";
export const RECAPTCHA_READY_POLL_MS = 50;
export const RECAPTCHA_READY_TIMEOUT_MS = 8000;
export const RECAPTCHA_EXECUTE_ATTEMPTS = 3;
export const RECAPTCHA_HIDDEN_BODY_CLASS = "ai-triage-recaptcha-hidden";

let recaptchaScriptPromise = null;

export function recaptchaSiteKey() {
  return window.AppConfig?.RECAPTCHA_SITE_KEY_V3 || null;
}

export function setRecaptchaBadgeVisible(visible) {
  document.body.classList.toggle(RECAPTCHA_HIDDEN_BODY_CLASS, !visible);
}

export function waitForGrecaptchaExecute(
  timeoutMs = RECAPTCHA_READY_TIMEOUT_MS,
  { now = Date.now, delay = (ms, cb) => window.setTimeout(cb, ms) } = {}
) {
  if (window.grecaptcha?.execute) {
    return Promise.resolve(window.grecaptcha);
  }

  return new Promise((resolve, reject) => {
    const startedAt = now();
    const poll = () => {
      if (window.grecaptcha?.execute) {
        resolve(window.grecaptcha);
        return;
      }
      if (now() - startedAt >= timeoutMs) {
        reject(new Error("reCAPTCHA failed to initialise"));
        return;
      }
      delay(RECAPTCHA_READY_POLL_MS, poll);
    };
    poll();
  });
}

export function loadRecaptchaScript() {
  const siteKey = recaptchaSiteKey();
  if (!siteKey) {
    return Promise.resolve(null);
  }

  if (window.grecaptcha?.execute) {
    return Promise.resolve(siteKey);
  }

  if (recaptchaScriptPromise) {
    return recaptchaScriptPromise;
  }

  recaptchaScriptPromise = new Promise((resolve, reject) => {
    const fail = (error) => {
      recaptchaScriptPromise = null;
      reject(error);
    };

    const existing = document.querySelector(`script[${RECAPTCHA_SCRIPT_ATTR}]`);
    if (existing) {
      waitForGrecaptchaExecute()
        .then(() => resolve(siteKey))
        .catch(fail);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(
      siteKey
    )}`;
    script.async = true;
    script.defer = true;
    script.setAttribute(RECAPTCHA_SCRIPT_ATTR, "true");
    script.onload = () => {
      waitForGrecaptchaExecute()
        .then(() => resolve(siteKey))
        .catch(fail);
    };
    script.onerror = () => fail(new Error("Failed to load reCAPTCHA"));
    document.head.appendChild(script);
  });

  return recaptchaScriptPromise;
}

export async function fetchRecaptchaToken(action = RECAPTCHA_ACTION) {
  const siteKey = recaptchaSiteKey();
  if (!siteKey) {
    return null;
  }

  await loadRecaptchaScript();
  const grecaptcha = await waitForGrecaptchaExecute();

  await new Promise((resolve) => {
    grecaptcha.ready(resolve);
  });

  let lastError;
  for (let attempt = 0; attempt < RECAPTCHA_EXECUTE_ATTEMPTS; attempt += 1) {
    try {
      const token = await grecaptcha.execute(siteKey, {
        action,
      });
      if (typeof token === "string" && token.trim().length > 0) {
        return token.trim();
      }
      lastError = new Error("reCAPTCHA returned an empty token");
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("reCAPTCHA execute failed");
}
